import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  width: 100%;
`;

const SvgWrapper = styled.div`
  width: 150px;
  height: 150px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${float} 3s ease-in-out infinite;
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 300px;
  height: 8px;
  background-color: #edf2f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressBar = styled.div<{ width: number; color?: string }>`
  height: 100%;
  width: ${props => props.width}%;
  background-color: ${props => props.color || '#e74c3c'};
  transition: width 0.5s ease-in-out;
`;

const StatusText = styled.p`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
  text-align: center;
`;

const SubText = styled.p`
  font-size: 0.9rem;
  color: #718096;
  text-align: center;
`;

const AnimatedCircle = styled.circle<{ duration?: string }>`
  animation: ${pulse} ${props => props.duration || '2s'} ease-in-out infinite;
`;

interface OrderProcessingAnimationProps {
  status?: string;
  progress: number;
  primaryColor?: string;
}

const OrderProcessingAnimation: React.FC<OrderProcessingAnimationProps> = ({ 
  status = 'PROCESSING', 
  progress,
  primaryColor = '#e74c3c' 
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // SVG Icons for different stages
  const renderIcon = () => {
    // We alternate icons to make it look dynamic like iFood
    const step = Math.floor(progress / 34); // 0, 1, 2
    
    switch (step) {
      case 0: // Processing / Received
        return (
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="15" width="50" height="70" rx="5" stroke={primaryColor} strokeWidth="4" />
            <line x1="35" y1="30" x2="65" y2="30" stroke={primaryColor} strokeWidth="4" strokeLinecap="round" />
            <line x1="35" y1="45" x2="65" y2="45" stroke={primaryColor} strokeWidth="4" strokeLinecap="round" />
            <line x1="35" y1="60" x2="55" y2="60" stroke={primaryColor} strokeWidth="4" strokeLinecap="round" />
            <AnimatedCircle cx="75" cy="80" r="10" fill={primaryColor} duration="2s" />
          </svg>
        );
      case 1: // Preparing
        return (
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 40H80V70C80 75.5228 75.5228 80 70 80H30C24.4772 80 20 75.5228 20 70V40Z" stroke={primaryColor} strokeWidth="4" />
            <path d="M25 40V30C25 24.4772 29.4772 20 35 20H65C70.5228 20 75 24.4772 75 30V40" stroke={primaryColor} strokeWidth="4" />
            <path d="M40 80V90M50 80V90M60 80V90" stroke="#CBD5E0" strokeWidth="4" strokeLinecap="round" />
            <path d="M35 55C35 55 40 50 50 50C60 50 65 55 65 55" stroke={primaryColor} strokeWidth="3" strokeLinecap="round" />
          </svg>
        );
      case 2: // Almost ready
      default:
        return (
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 30L50 15L80 30V70L50 85L20 70V30Z" stroke={primaryColor} strokeWidth="4" />
            <path d="M50 15V85M20 30L80 70M80 30L20 70" stroke={primaryColor} strokeWidth="2" strokeOpacity="0.3" />
            <AnimatedCircle cx="50" cy="50" r="15" fill={primaryColor} duration="1.5s" />
            <path d="M42 50L48 56L58 44" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
    }
  };

  const getStatusMessage = () => {
    if (progress < 30) return 'Recebendo seu pedido...';
    if (progress < 70) return 'Seu pedido está sendo processado...';
    return 'Quase lá! Finalizando a criação...';
  };

  return (
    <Container>
      <SvgWrapper>
        {renderIcon()}
      </SvgWrapper>
      <StatusText>{getStatusMessage()}</StatusText>
      <ProgressContainer>
        <ProgressBar width={progress} color={primaryColor} />
      </ProgressContainer>
      <SubText>Aguarde um momento enquanto conectamos com o restaurante.</SubText>
    </Container>
  );
};

export default OrderProcessingAnimation;
